import { useState } from 'react'
import { Button, Card, FileInput, Group, List, Modal, Stack, Table, Text, Textarea, ThemeIcon } from '@mantine/core'
import backend from '../../backend/backend'
import { useAction, useT } from '../../hooks'
import Message from '../../i18n/Message'
import { formatDate, naira } from '../../utils/format'
import StatusBadge from './StatusBadge'
import Money from './Money'

/** Asks for a reason (or note) before running a consequential action. */
export function ReasonModal({ opened, title, label = 'reason', required = true, onClose, onSubmit, busy }) {
  const t = useT()
  const [value, setValue] = useState('')
  return (
    <Modal opened={opened} onClose={onClose} title={<Message id={title} />} centered>
      <Stack>
        <Textarea label={t(label)} value={value} onChange={(e) => setValue(e.currentTarget.value)} autosize minRows={3} data-autofocus />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            <Message id="cancel" />
          </Button>
          <Button
            loading={busy}
            disabled={required && !value.trim()}
            onClick={async () => {
              await onSubmit(value.trim())
              setValue('')
            }}
          >
            <Message id="confirm" />
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

/** One document slot per type: upload (camera for photos), status, and a view link. */
export function DocumentUploader({ customerId, applicationId, docType, documents = [], onChange }) {
  const t = useT()
  const { busy, run } = useAction()
  const mine = documents.filter((d) => d.doc_type === docType)
  return (
    <Stack gap={6}>
      <Group justify="space-between" wrap="nowrap">
        <Text fw={500}>
          <Message id={`doc_${docType}`} />
        </Text>
        {mine[0] && <StatusBadge status={mine[0].status} />}
      </Group>
      <FileInput
        placeholder={t('doc_choose')}
        accept={docType === 'customer_photo' ? 'image/*' : 'image/jpeg,image/png,image/webp,application/pdf'}
        capture={docType === 'customer_photo' ? 'user' : undefined}
        disabled={busy}
        onChange={(file) => {
          if (!file) return
          run(() => backend.uploadDocument(customerId, file, docType, applicationId), { success: 'doc_uploaded', onDone: onChange })
        }}
      />
      {mine.map((d) => (
        <Group key={d.id} gap="xs">
          <Text size="sm" component="a" href={backend.documentUrl(d.id)} target="_blank" rel="noreferrer" c="brand">
            {d.original_filename}
          </Text>
          {d.review_note && (
            <Text size="xs" c="dimmed">
              {d.review_note}
            </Text>
          )}
        </Group>
      ))}
    </Stack>
  )
}

/** Renders the backend quote. The UI never recomputes the maths. */
export function QuotePanel({ quote, error }) {
  if (error) {
    return (
      <Card>
        <Text c="red" size="sm">
          {error}
        </Text>
      </Card>
    )
  }
  if (!quote) {
    return (
      <Card>
        <Text c="dimmed" size="sm">
          <Message id="quote_hint" />
        </Text>
      </Card>
    )
  }
  const row = (id, kobo, strong) => (
    <Group justify="space-between" key={id}>
      <Text size="sm" fw={strong ? 600 : 400}>
        <Message id={id} />
      </Text>
      <Money kobo={kobo} fw={strong ? 600 : 400} />
    </Group>
  )
  return (
    <Card>
      <Stack gap={6}>
        {quote.priceKobo ? row('quote_price', quote.priceKobo) : null}
        {quote.priceKobo ? row('quote_deposit', quote.depositKobo) : null}
        {row('quote_financed', quote.principalKobo)}
        {row('quote_interest', quote.interestKobo)}
        {row('quote_total', quote.totalKobo, true)}
        <Text size="sm" c="dimmed" mt={4}>
          <Message id="quote_installments" values={{ count: quote.termMonths }} />
        </Text>
        {quote.installmentsKobo.map((k, i) => (
          <Group justify="space-between" key={i}>
            <Text size="sm">
              <Message id="quote_month" values={{ n: i + 1 }} />
            </Text>
            <Money kobo={k} />
          </Group>
        ))}
      </Stack>
    </Card>
  )
}

export function ScheduleTable({ installments = [] }) {
  return (
    <Table.ScrollContainer minWidth={520}>
      <Table verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            {['col_number', 'col_due', 'col_amount', 'col_paid', 'col_late_fee', 'col_status'].map((h) => (
              <Table.Th key={h}>
                <Message id={h} />
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {installments.map((i) => (
            <Table.Tr key={i.id}>
              <Table.Td>{i.seq}</Table.Td>
              <Table.Td>{formatDate(i.due_date)}</Table.Td>
              <Table.Td className="num">{naira(i.amount_kobo)}</Table.Td>
              <Table.Td className="num">{naira(i.paid_kobo)}</Table.Td>
              <Table.Td className="num">{naira(Math.max(0, i.late_fee_kobo - i.late_fee_waived_kobo))}</Table.Td>
              <Table.Td>
                <StatusBadge status={i.status} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

export function ApprovalChecklist({ checklist }) {
  if (!checklist) return null
  return (
    <List spacing="xs" size="sm" center>
      {checklist.items.map((i) => (
        <List.Item
          key={i.key}
          icon={
            <ThemeIcon color={i.ok ? 'green' : 'red'} size={22} radius="xl" variant="light">
              <Text size="xs" fw={700} component="span">
                {i.ok ? '✓' : '✕'}
              </Text>
            </ThemeIcon>
          }
        >
          {i.detail}
        </List.Item>
      ))}
    </List>
  )
}
